import moment from "moment"
import { PropsWithChildren, useContext, useEffect, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { FormType } from "../utils"
import { Loading, PrintTransactionModal, TableHeader } from "components"
import { convertNumber, useCountryById, useEmployee, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById } from "utils"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useSupplier } from "pages/Purchase/Transaction/components/Supplier/utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { DetailDataContext, ProductProvider } from "../contexts"
import { ProductUnitProvider } from "pages/Purchase/Transaction/EnterBill/contexts"

export function PrintAdjustmentCountInventory(params: { isCreate?: boolean, toggle: () => void }): JSX.Element {
  const { control } = useFormContext<FormType>()

  const { invcount, countdetail } = useContext(DetailDataContext)

  const employee_id = useWatch({ control, name: "employee_id" })
  const transaction_date = useWatch({ control, name: "transaction_date" })
  const trx_code = useWatch({ control, name: "trx_code" })

  const dataEmployee = useEmployee()
  const findEmployee = dataEmployee?.dataEmployee?.find((item) => item?.value === employee_id)?.label

  const setting = useSubdomainSetting()
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()

  const { userTokenData } = useHandleAuth()
  const userToken = userTokenData()

  const [address, setAddress] = useState<string>("")

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }

  const isDataEmpty = <tr><td colSpan={13}><Loading errorText="No data available" /></td></tr>

  return (
    <PrintTransactionModal
      isLandscape
      isLoading={company.isLoading || supplier.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading}
      toggle={params.toggle}
    >
      <section className="flex flex-col gap-3">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems uppercase label="ADDRESS" text={getAddress()} />
                <InfoItems label="PHONE" text={setting.data?.phone!} />
                <InfoItems label="EMAIL" text={setting.data?.email!} />
                <InfoItems label="WEBSITE" text={setting.data?.website!} />
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">
              ADJUSTMENT INVENTORY
            </div>

            <table>
              <tbody className="font-bold ">
                <InfoItems width={280} textRight label="DATE" text={moment(transaction_date).format("DD/MM/YYYY")} />
                <InfoItems width={280} textRight label="ADJUSTMENT INVENTORY NO" text={trx_code} />
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <TableHeader>
            <tr>
              <th rowSpan={2}>#</th>
              <th rowSpan={2}>ITEM</th>
              <th rowSpan={2}>DESCRIPTION</th>
              <th rowSpan={2}>UNIT</th>
              <th rowSpan={2}>SOH</th>
              <th rowSpan={2}>STOCK IN</th>
              <th rowSpan={2}>STOCK OUT</th>
              <th colSpan={2}>RECAP IN CONVERTION UNIT</th>
              <th rowSpan={2}>AVERAGE UNIT PRICE</th>
              <th rowSpan={2}>AMOUNT ADJUSTMENT</th>
              <th rowSpan={2}>POSITION</th>
              <th rowSpan={2}>COA INVENTORY</th>
              <th rowSpan={2}>COA EXPENCES/COGS</th>
            </tr>
            <tr>
              <th>IN</th>
              <th>OUT</th>
            </tr>
          </TableHeader>
          <ProductProvider>
            <ProductUnitProvider>
              <tbody>
                {!countdetail?.length ? isDataEmpty : (
                  countdetail?.map((item, key) => (
                    <ItemCreate key={key} index={key} item={item} />
                  ))
                )}
              </tbody>

            </ProductUnitProvider>
          </ProductProvider>
        </table>

        <section>
          <table className="table-top">
            <tbody>
              <InfoItems width={180} label="REQUEST BY" text={invcount?.employee_name} />
              <InfoItems width={180} label="ADJUSTMENT BY" text={findEmployee!} />
            </tbody>
          </table>
        </section>
      </section>
    </PrintTransactionModal>
  )
}

type InfoItemsType = {
  label: string
  text: string
  uppercase?: boolean
  textRight?: boolean
  width?: number
}
const InfoItems = (props: PropsWithChildren<InfoItemsType>) => (
  <tr>
    <td width={props?.width ? props?.width : 120} className="pr-2 uppercase flex justify-between">
      <div>{props?.label}</div>
      <div>:</div>
    </td>
    <td className={props?.uppercase ? `uppercase` : props?.textRight ? "text-right" : ""}>{props?.text || "-"}</td>
  </tr>
)
function ItemCreate(params: { index: number, item: any }): JSX.Element {
  const item = params.item
  const soh = item.soh_count ?? item.soh
  const recapUnit = Math.abs(Number(item.different_unit) || 0)
  const isDebet = item.position === "debet"

  return (
    <tr>
      <td>{params.index + 1}</td>
      <td>{item?.product_barcode}</td>
      <td>{item?.product_name}</td>
      <td>{item?.mr_unit_name}</td>
      <td>{soh != null ? convertNumber(soh).intoNormalAmount : 0}</td>
      <td className="text-right">{convertNumber(Math.abs(item.stock_in || 0)).intoNormalAmount}</td>
      <td className="text-right">{convertNumber(Math.abs(item.stock_out || 0)).intoNormalAmount}</td>
      <td className="text-right">{isDebet && recapUnit ? `${convertNumber(recapUnit).intoNormalAmount} ${item.unit_convention_name ?? ""}` : 0}</td>
      <td className="text-right">{!isDebet && recapUnit ? `${convertNumber(recapUnit).intoNormalAmount} ${item.unit_convention_name ?? ""}` : 0}</td>
      <td className="text-right">{convertNumber(Number(item.avg_price_use) || 0).intoCurrency}</td>
      <td className="text-right">{convertNumber(Number(item.amount_adjustment)).intoCurrency}</td>
      <td className="uppercase">{item.position}</td>
      <td>{` ${item.coa_inventory_code} - ${item.coa_inventory_name}`}</td>
      <td>{` ${item.coa_coex_code} - ${item.coa_coex_name}`}</td>
    </tr>
  )
}